import React, { useState, useEffect } from 'react';
import { collection, query, where, getDocs, doc, updateDoc, addDoc, arrayUnion, Timestamp, onSnapshot } from 'firebase/firestore';
import { createNotification } from '../../services/NotificationSystem';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { db } from '../../firebase/config';
import { ToothChart, toothChartHtml, shadesOf, groupDetails, materialsOf } from './ToothChart';
import { Package, CheckCircle, RotateCcw, RefreshCw, Send, Printer } from 'lucide-react';

// 🌏 해외 발주측 케이스 (영문) — 에이전트 감지 초안(globalDrafts) 검토·보내기 + 주문 현황
const STATUS = {
    queued: { label: 'Sending files…', bg: '#EEF0FA', color: '#4F52C7' },
    sent: { label: 'Sent', bg: '#EEF0FA', color: '#4F52C7' },
    accepted: { label: 'Accepted', bg: '#E6F5F1', color: '#0B6E5D' },
    working: { label: 'In progress', bg: '#E6F5F1', color: '#0B6E5D' },
    returned: { label: 'Design returned', bg: '#FDF3E3', color: '#B45309' },
    confirmed: { label: 'Confirmed', bg: '#E6F5F1', color: '#0B6E5D' },
    revision: { label: 'Revision requested', bg: '#FEF2F2', color: '#DC2626' },
    declined: { label: 'Declined', bg: '#FEF2F2', color: '#DC2626' },
    canceled: { label: 'Canceled', bg: '#F1F5F9', color: '#64748B' },
};
const defaultDue = () => {
    const d = new Date(Date.now() + 2 * 24 * 3600 * 1000);
    return d.toISOString().slice(0, 10);
};


// FDI(11-48) → Universal(1-32, 미국식) 변환. 유치·비정상 값은 원본 유지
const fdiToUni = (t) => {
    const n = parseInt(t, 10);
    if (isNaN(n)) return null;
    const q = Math.floor(n / 10), d = n % 10;
    if (d < 1 || d > 8) return null;
    if (q === 1) return 9 - d;
    if (q === 2) return 8 + d;
    if (q === 3) return 25 - d;
    if (q === 4) return 24 + d;
    return null;
};
const uniToFdi = (u) => {
    const n = parseInt(u, 10);
    if (isNaN(n) || n < 1 || n > 32) return null;
    if (n <= 8) return 10 + (9 - n);
    if (n <= 16) return 20 + (n - 8);
    if (n <= 24) return 30 + (25 - n);
    return 40 + (n - 24);
};
const teethToUniList = (arr) => (arr || [])
    .map(t => ({ fdi: t, uni: fdiToUni(t) }))
    .filter(x => x.uni !== null)
    .sort((a, b) => a.uni - b.uni);
const uniStr = (arr) => {
    const l = teethToUniList(arr);
    return l.length ? l.map(x => x.uni).join(',') : (arr || []).join(',');
};
const sortDetailsUni = (details) => [...(details || [])].sort((a, b) => (fdiToUni(a.tooth) ?? 99) - (fdiToUni(b.tooth) ?? 99));

// 서버 번역(translateText) 헬퍼 — 실패 시 빈 문자열 반환 (호출측 흐름을 막지 않음)
const tryTranslateKo = async (text) => {
    if (!text) return '';
    try {
        const fn = httpsCallable(getFunctions(undefined, 'asia-northeast3'), 'translateText');
        const res = await fn({ text, targetLang: 'ko' });
        return res?.data?.translated || '';
    } catch (e) {
        console.warn('선번역 실패 — 원문만 저장:', e);
        return '';
    }
};

function GlobalOrders({ user }) {
    const myUid = user?.companyId || user?.uid;
    const [orders, setOrders] = useState([]);
    const [drafts, setDrafts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [busyId, setBusyId] = useState(null);
    const [revisionFor, setRevisionFor] = useState(null);
    const [revisionReason, setRevisionReason] = useState('');
    const [draftFor, setDraftFor] = useState(null);
    const [draftNote, setDraftNote] = useState('');
    const [draftDue, setDraftDue] = useState(defaultDue());
    const [draftTeeth, setDraftTeeth] = useState('');          // Universal 번호 입력 (수정 가능)
    const [draftRestoration, setDraftRestoration] = useState('');
    const [draftMaterial, setDraftMaterial] = useState('');
    const [sheetFor, setSheetFor] = useState(null);   // 의뢰서 모달 (영문)
    const [folderMap, setFolderMap] = useState({});   // caseNo → 내 폴더명 (globalDrafts — 발주자만 읽는 문서)
    const [draftPatientRef, setDraftPatientRef] = useState('');

    const load = () => {};   // 실시간 구독으로 대체 (버튼 호환용 no-op)
    useEffect(() => {
        if (!myUid) return;
        setLoading(true);
        // 주문·초안 실시간 구독 — 상태 변화(queued→sent→accepted…)가 새로고침 없이 반영
        const unsubOrders = onSnapshot(query(collection(db, 'outsourceOrders'), where('fromUserId', '==', myUid)), (snap) => {
            const list = snap.docs.map(d => ({ id: d.id, ...d.data() })).filter(o => o.source === 'agent_auto');
            list.sort((a, b) => (b.createdAt?.seconds || 0) - (a.createdAt?.seconds || 0));
            setOrders(list);
            setLoading(false);
        }, () => setLoading(false));
        const unsubDrafts = onSnapshot(query(collection(db, 'globalDrafts'), where('fromUserId', '==', myUid)), (snap) => {
            const all = snap.docs.map(d => ({ id: d.id, ...d.data() }));
            const dList = all.filter(d => d.status === 'draft');
            dList.sort((a, b) => (b.createdAt?.seconds || 0) - (a.createdAt?.seconds || 0));
            setDrafts(dList);
            // caseNo → 폴더명 매핑 (모든 상태의 초안 이력 — 발주자 전용)
            const map = {};
            all.forEach(d => { if (d.caseNo && d.folderName) map[d.caseNo] = d.folderName; });
            setFolderMap(map);
        });
        return () => { unsubOrders(); unsubDrafts(); };
        // eslint-disable-next-line
    }, [myUid]);

    // 초안 → [Send to lab]: 주문 생성 + 에이전트에 파일 업로드 신호
    const sendDraft = async () => {
        const d = draftFor;
        if (!d) return;
        setBusyId(d.id);
        try {
            // 수정된 치식(Universal) → FDI. 기존 파싱 상세는 유지, 추가된 치아는 입력값으로 생성
            const uniNums = draftTeeth.split(/[\s,]+/).map(x => parseInt(x, 10)).filter(n => !isNaN(n));
            const fdiList = [...new Set(uniNums.map(uniToFdi).filter(Boolean))].map(String);
            const teeth = fdiList.length ? fdiList : (d.teeth || []);
            const prevDetails = d.teethDetails || [];
            const teethDetails = teeth.map(t =>
                prevDetails.find(x => String(x.tooth) === String(t)) ||
                { tooth: t, prosthesis: draftRestoration.trim(), material: draftMaterial.trim(), shade: '', implant: '', isPontic: false, isBridge: false });
            // 요청 메모 한국어 선번역 — 실패해도 주문 생성은 진행 (원문만 저장, 수주측에서 수동 번역 가능)
            const requestNoteKo = await tryTranslateKo(draftNote.trim());
            const orderRef = await addDoc(collection(db, 'outsourceOrders'), {
                orderType: 'design',
                source: 'agent_auto',
                caseNo: d.caseNo,
                agentLinkId: d.linkId || '',
                fromUserId: myUid,
                fromUserName: d.fromUserName || user?.businessName || '',
                toUserId: d.toUserId,
                toUserName: d.toUserName || '',
                participants: [myUid, d.toUserId],
                bundles: [{
                    patientName: draftPatientRef.trim() || d.caseNo,
                    implantType: draftMaterial.trim() || d.material || '',
                    shade: (d.teethDetails?.[0]?.shade) || '',
                    toothCount: String(teeth.length || ''),
                    implantCount: '',
                    files: [],
                }],
                teethDetails,
                indication: draftRestoration.trim() || d.indication || '',
                requestNote: draftNote.trim(),
                ...(requestNoteKo ? { requestNoteKo } : {}),   // 선번역 성공 시에만 저장
                patientRef: draftPatientRef.trim(),
                designRev: 0,
                status: 'queued',
                sendRequested: true,          // 에이전트가 감지 → 파일 zip 업로드 → status 'sent'
                dueDate: Timestamp.fromDate(new Date(draftDue + 'T09:00:00')),
                autoConfirmDays: 3,
                createdAt: new Date(),
                updatedAt: new Date(),
            });
            await updateDoc(doc(db, 'globalDrafts', d.id), { status: 'sending', orderId: orderRef.id, updatedAt: new Date() });
            // 국내 기공소 웹 알림함에도 표시 (트레이 알림과 별개)
            try {
                await createNotification({
                    recipientId: d.toUserId,
                    type: 'global_case',
                    title: '새 글로벌 케이스',
                    message: `${d.fromUserName || '해외 발주처'} — ${d.caseNo} 케이스가 도착합니다.`,
                    link: '/global-lab',
                    metadata: { caseNo: d.caseNo },
                });
            } catch (e) { /* 알림 실패는 무시 */ }
            setDraftFor(null); setDraftNote('');
            load();
        } catch (e) {
            console.error('보내기 실패:', e);
            alert('Failed to send. Please try again.');
        } finally { setBusyId(null); }
    };

    const discardDraft = async (d) => {
        if (!window.confirm(`Discard ${d.caseNo}? The files stay on your PC — nothing is sent.`)) return;
        try {
            await updateDoc(doc(db, 'globalDrafts', d.id), { status: 'discarded', updatedAt: new Date() });
            load();
        } catch (e) { alert('Failed.'); }
    };

    const confirm = async (o) => {
        if (!window.confirm(`Confirm the returned design for ${o.caseNo}? This finalizes the case for invoicing.`)) return;
        setBusyId(o.id);
        try {
            await updateDoc(doc(db, 'outsourceOrders', o.id), {
                status: 'confirmed', confirmedAt: new Date(), confirmedBy: myUid, updatedAt: new Date(),
            });
            load();
        } catch (e) { alert('Failed. Please try again.'); }
        finally { setBusyId(null); }
    };

    const requestRevision = async () => {
        const o = revisionFor;
        if (!o || !revisionReason.trim()) return;
        setBusyId(o.id);
        try {
            const rev = (o.designRev || 0) + 1;
            // 수정 사유 한국어 선번역 — 실패해도 수정 요청은 진행 (수주측 revisionsKo 캐시와 동일 위치에 저장)
            const reasonKo = await tryTranslateKo(revisionReason.trim());
            await updateDoc(doc(db, 'outsourceOrders', o.id), {
                status: 'revision',
                revisions: arrayUnion({ rev, reason: revisionReason.trim(), requestedAt: new Date() }),
                ...(reasonKo ? { [`revisionsKo.${rev}`]: reasonKo } : {}),
                updatedAt: new Date(),
            });
            setRevisionFor(null); setRevisionReason('');
            load();
        } catch (e) { alert('Failed. Please try again.'); }
        finally { setBusyId(null); }
    };

    const fmt = (v) => v?.toDate ? v.toDate().toLocaleDateString('en-US') : '-';

    // returned 상태의 자동 확정 예정일 — 백엔드(scheduledGlobalAutoConfirm)가 쓰는 autoConfirmAt만 신뢰.
    // autoConfirmAt이 없으면 서버가 자동확정하지 않으므로 카운트다운을 표시하지 않음 (거짓 경고 방지)
    const autoConfirmDate = (o) => (o.autoConfirmAt?.toDate ? o.autoConfirmAt.toDate() : null);

    // 인쇄/PDF — 영문 의뢰서 시트 새 창
    const openOrderSheet = (o) => {
        const st = STATUS[o.status] || STATUS.sent;
        const details = sortDetailsUni(o.teethDetails);
        const groups = groupDetails(details);
        const uniTeeth = (teeth) => teeth.map(t => fdiToUni(t) ?? t).sort((a, b) => a - b);
        const rows = groups.length
            ? groups.map(g => `<tr>
                <td style="font-weight:700">${uniTeeth(g.teeth).join(', ')} <span style="color:#94a3b8;font-weight:400;font-size:11px">(FDI ${g.teeth.join(', ')})</span></td>
                <td>${g.isPontic ? 'Pontic' : (g.prosthesis || o.indication || '-')}</td>
                <td>${g.material || o.bundles?.[0]?.implantType || '-'}</td><td>${g.shade || '-'}</td>
                <td>${[g.implant && ('Implant: ' + g.implant), g.isBridge && 'Bridge'].filter(Boolean).join(' · ')}</td>
              </tr>`).join('')
            : `<tr><td colspan="5" style="color:#94a3b8">No tooth details — see files</td></tr>`;
        const html = `<!doctype html><html><head><meta charset="utf-8"><title>Order ${o.caseNo}</title>
<style>body{font-family:-apple-system,sans-serif;color:#1e2230;margin:40px;font-size:14px}
h1{font-size:20px;margin:0 0 2px}.sub{color:#64748b;font-size:12.5px;margin-bottom:20px}
.meta{display:grid;grid-template-columns:repeat(2,1fr);gap:6px 24px;border:1px solid #e2e8f0;border-radius:10px;padding:14px 18px;margin-bottom:18px}
.meta b{display:inline-block;min-width:80px;color:#64748b;font-weight:600}
table{width:100%;border-collapse:collapse;margin-bottom:18px}
th,td{border:1px solid #e2e8f0;padding:8px 10px;text-align:left;font-size:13px}th{background:#f8fafc;color:#64748b;font-size:12px}
.note{border:1px solid #e2e8f0;border-radius:10px;padding:12px 16px;line-height:1.7;margin-bottom:14px}
.foot{color:#94a3b8;font-size:11.5px;margin-top:24px;border-top:1px solid #e2e8f0;padding-top:10px}
@media print{body{margin:14mm}}</style></head><body>
<h1>Design Order <span style="font-family:ui-monospace,monospace">${o.caseNo}</span></h1>
<div class="sub">DentCluster Global · Status: ${st.label}${(o.designRev || 0) > 0 ? ` · r${o.designRev}` : ''}</div>
<div class="meta">
  <div><b>From</b> ${o.fromUserName || ''}</div><div><b>Lab</b> ${o.toUserName || ''}</div>
  <div><b>Sent</b> ${fmt(o.createdAt)}</div><div><b>Due</b> ${fmt(o.dueDate)}</div>
  <div><b>Type</b> ${o.indication || '-'}</div><div><b>Material</b> ${materialsOf(o.teethDetails, o.bundles?.[0]?.implantType)}</div>
</div>
<div style="display:flex;align-items:center;gap:22px;margin-bottom:16px;flex-wrap:wrap">${toothChartHtml(details.map(d => d.tooth))}${shadesOf(details).length ? `<div style="font-size:14px"><b>Shade:</b> ${shadesOf(details).join(', ')}</div>` : ''}</div>
<table><thead><tr><th>Tooth</th><th>Restoration</th><th>Material</th><th>Shade</th><th>Notes</th></tr></thead><tbody>${rows}</tbody></table>
${o.requestNote ? `<div class="note"><b>Notes</b><br/>${o.requestNote.replace(/\n/g, '<br/>')}</div>` : ''}
<div class="foot">Tooth numbers use the Universal (US) system; FDI in parentheses · Patient identifiers are replaced with the case number for privacy · Printed ${new Date().toLocaleDateString('en-US')}</div>
<script>window.onload=function(){window.print()}</script></body></html>`;
        const w = window.open('', '_blank');
        if (!w) { alert('Popup blocked — please allow popups for this site.'); return; }
        w.document.write(html);
        w.document.close();
    };

    return (
        <div style={{ maxWidth: 900, margin: '0 auto', padding: 20 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <h1 style={{ fontSize: 22, fontWeight: 800, color: '#1E2230' }}><Package size={20} style={{ verticalAlign: -2 }} /> My Cases</h1>
                <button onClick={load} style={{ border: '1px solid #E4E6EE', background: '#fff', borderRadius: 8, padding: '7px 12px', cursor: 'pointer', fontSize: 13, color: '#5A6072', display: 'inline-flex', alignItems: 'center', gap: 5 }}><RefreshCw size={13} /> Refresh</button>
            </div>
            <p style={{ fontSize: 13.5, color: '#5A6072', margin: '4px 0 18px' }}>
                When your Agent detects a case folder, it appears here as a <b>draft with the order form pre-filled</b>.
                Review it, then press <b>Send</b> — files and the order transfer together. Nothing is uploaded until you send.
            </p>

            {/* 초안 — 검토 후 보내기 */}
            {drafts.length > 0 && (
                <div style={{ marginBottom: 18 }}>
                    <div style={{ fontSize: 13, fontWeight: 800, color: '#B45309', marginBottom: 8 }}>DRAFTS — REVIEW & SEND ({drafts.length})</div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                        {drafts.map(d => (
                            <div key={d.id} style={{ background: '#FFFBEB', border: '1px solid #F0DCBB', borderRadius: 12, padding: '13px 16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                                <div>
                                    <span style={{ fontWeight: 800, fontFamily: 'ui-monospace, monospace' }}>{d.caseNo}</span>
                                    {d.folderName && <span style={{ marginLeft: 8, fontSize: 12.5, fontWeight: 700, color: '#1E2230' }}>📁 {d.folderName}</span>}
                                    <span style={{ marginLeft: 10, fontSize: 13, color: '#5A6072' }}>{d.toUserName || 'Lab'}</span>
                                    <span style={{ marginLeft: 10, fontSize: 12.5, color: '#94A3B8' }}>
                                        Teeth {uniStr(d.teeth) || '-'}{d.indication ? ` · ${d.indication}` : ''}{materialsOf(d.teethDetails, d.material) !== '-' ? ` · ${materialsOf(d.teethDetails, d.material)}` : ''}
                                    </span>
                                </div>
                                <div style={{ display: 'flex', gap: 8 }}>
                                    <button onClick={() => discardDraft(d)}
                                        style={{ padding: '7px 12px', borderRadius: 8, border: '1px solid #E4E6EE', background: '#fff', color: '#94A3B8', fontSize: 12.5, fontWeight: 700, cursor: 'pointer' }}>Discard</button>
                                    <button onClick={() => { setDraftFor(d); setDraftNote(''); setDraftDue(defaultDue()); setDraftTeeth(uniStr(d.teeth)); setDraftRestoration(d.indication || ''); setDraftMaterial(d.material || ''); setDraftPatientRef(''); }}
                                        style={{ padding: '7px 14px', borderRadius: 8, border: 'none', background: '#4F52C7', color: '#fff', fontSize: 12.5, fontWeight: 700, cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 5 }}>
                                        <Send size={13} /> Review & send
                                    </button>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {loading ? <div style={{ color: '#5A6072', padding: 30 }}>Loading…</div> : orders.length === 0 && drafts.length === 0 ? (
                <div style={{ textAlign: 'center', color: '#94A3B8', padding: '50px 0', background: '#fff', border: '1px solid #E4E6EE', borderRadius: 12 }}>
                    No cases yet. Put a case folder into your Agent's sending folder — it will appear here as a draft.
                </div>
            ) : orders.length > 0 && (
                <div style={{ background: '#fff', border: '1px solid #E4E6EE', borderRadius: 12, overflowX: 'auto' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13.5 }}>
                        <thead><tr style={{ background: '#F8FAFC' }}>
                            {['Case #', 'Lab', 'Teeth', 'Sent', 'Due', 'Rev', 'Status', 'Action'].map(h =>
                                <th key={h} style={{ textAlign: 'left', padding: '10px 14px', fontSize: 12, color: '#64748B', borderBottom: '1px solid #E4E6EE', whiteSpace: 'nowrap' }}>{h}</th>)}
                        </tr></thead>
                        <tbody>
                            {orders.map(o => {
                                const st = STATUS[o.status] || STATUS.sent;
                                const teeth = uniStr((o.teethDetails || []).map(t => t.tooth).filter(Boolean));
                                return (
                                    <tr key={o.id} onClick={() => setSheetFor(o)} style={{ borderBottom: '1px solid #F1F5F9', cursor: 'pointer' }}>
                                        <td style={{ padding: '10px 14px', whiteSpace: 'nowrap' }}>
                                            <div style={{ fontWeight: 700, fontFamily: 'ui-monospace, monospace' }}>{o.caseNo}</div>
                                            {(o.patientRef || folderMap[o.caseNo]) && <div style={{ fontSize: 11.5, color: '#64748B' }}>📁 {o.patientRef || folderMap[o.caseNo]}</div>}
                                        </td>
                                        <td style={{ padding: '10px 14px' }}>{o.toUserName || '-'}</td>
                                        <td style={{ padding: '10px 14px', color: '#5A6072' }}>{teeth || '-'}</td>
                                        <td style={{ padding: '10px 14px', color: '#5A6072', whiteSpace: 'nowrap' }}>{fmt(o.createdAt)}</td>
                                        <td style={{ padding: '10px 14px', color: '#5A6072', whiteSpace: 'nowrap' }}>{fmt(o.dueDate)}</td>
                                        <td style={{ padding: '10px 14px', color: '#5A6072' }}>r{o.designRev || 0}</td>
                                        <td style={{ padding: '10px 14px' }}>
                                            <span style={{ fontSize: 11.5, fontWeight: 700, padding: '3px 9px', borderRadius: 99, background: st.bg, color: st.color, whiteSpace: 'nowrap' }}>{st.label}</span>
                                            {/* returned 행에 자동 확정 예정일 표시 (계산 불가 시 고정 문구 폴백) */}
                                            {o.status === 'returned' && (
                                                <div style={{ fontSize: 11, color: '#B45309', marginTop: 3, whiteSpace: 'nowrap' }}>
                                                    {autoConfirmDate(o) ? `Auto-confirms on ${autoConfirmDate(o).toLocaleDateString('en-US')}` : 'Please review and confirm'}
                                                </div>
                                            )}
                                        </td>
                                        <td style={{ padding: '10px 14px', whiteSpace: 'nowrap' }} onClick={e => e.stopPropagation()}>
                                            {o.status === 'returned' && (
                                                <>
                                                    <button disabled={busyId === o.id} onClick={() => confirm(o)}
                                                        style={{ marginRight: 6, padding: '6px 10px', borderRadius: 7, border: '1px solid #0E8A74', background: '#fff', color: '#0E8A74', fontSize: 12, fontWeight: 700, cursor: 'pointer' }}>
                                                        <CheckCircle size={12} style={{ verticalAlign: -2 }} /> Confirm
                                                    </button>
                                                    <button disabled={busyId === o.id} onClick={() => { setRevisionFor(o); setRevisionReason(''); }}
                                                        style={{ padding: '6px 10px', borderRadius: 7, border: '1px solid #DC2626', background: '#fff', color: '#DC2626', fontSize: 12, fontWeight: 700, cursor: 'pointer' }}>
                                                        <RotateCcw size={12} style={{ verticalAlign: -2 }} /> Revision
                                                    </button>
                                                </>
                                            )}
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            )}

            {/* 의뢰서 모달 — 행 클릭 시 (영문) */}
            {sheetFor && (() => {
                const o = sheetFor;
                const st = STATUS[o.status] || STATUS.sent;
                const details = sortDetailsUni(o.teethDetails);
                const th = { border: '1px solid #E4E6EE', padding: '7px 10px', textAlign: 'left', background: '#F8FAFC', color: '#64748B', fontSize: 12 };
                const td = { border: '1px solid #E4E6EE', padding: '7px 10px' };
                return (
                    <div style={{ position: 'fixed', inset: 0, background: 'rgba(15,23,42,.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 50, padding: 16 }} onClick={() => setSheetFor(null)}>
                        <div style={{ background: '#fff', borderRadius: 14, width: '100%', maxWidth: 560, maxHeight: '88vh', overflowY: 'auto', padding: '22px 24px' }} onClick={e => e.stopPropagation()}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 4 }}>
                                <div>
                                    <div style={{ fontSize: 18, fontWeight: 800, fontFamily: 'ui-monospace, monospace' }}>{o.caseNo}</div>
                                    <div style={{ fontSize: 12.5, color: '#5A6072', marginTop: 2 }}>
                                        Design order · <span style={{ fontWeight: 700, color: st.color }}>{st.label}</span>{(o.designRev || 0) > 0 ? ` · r${o.designRev}` : ''}
                                        {(o.patientRef || folderMap[o.caseNo]) && <span style={{ marginLeft: 8 }}>📁 {o.patientRef || folderMap[o.caseNo]}</span>}
                                    </div>
                                </div>
                                <button onClick={() => setSheetFor(null)} style={{ border: 'none', background: 'none', cursor: 'pointer', color: '#94A3B8', fontSize: 18 }}>✕</button>
                            </div>
                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '6px 18px', border: '1px solid #E4E6EE', borderRadius: 10, padding: '12px 16px', margin: '12px 0', fontSize: 13.5 }}>
                                <div><span style={{ color: '#94A3B8', fontSize: 12.5 }}>Lab</span> {o.toUserName || '-'}</div>
                                <div><span style={{ color: '#94A3B8', fontSize: 12.5 }}>Due</span> <b>{fmt(o.dueDate)}</b></div>
                                <div><span style={{ color: '#94A3B8', fontSize: 12.5 }}>Sent</span> {fmt(o.createdAt)}</div>
                                <div><span style={{ color: '#94A3B8', fontSize: 12.5 }}>Material</span> {materialsOf(o.teethDetails, o.bundles?.[0]?.implantType)}</div>
                            </div>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 20, marginBottom: 12, flexWrap: 'wrap' }}>
                                <ToothChart teeth={details.map(d => d.tooth)} />
                                {shadesOf(details).length > 0 && <div style={{ fontSize: 13.5 }}><b>Shade:</b> {shadesOf(details).join(', ')}</div>}
                            </div>
                            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13, marginBottom: 12 }}>
                                <thead><tr>{['Tooth', 'Restoration', 'Material', 'Shade', 'Notes'].map(h => <th key={h} style={th}>{h}</th>)}</tr></thead>
                                <tbody>
                                    {details.length === 0 ? (
                                        <tr><td colSpan={5} style={{ ...td, color: '#94A3B8' }}>No tooth details — see files</td></tr>
                                    ) : groupDetails(details).map((g, i) => (
                                        <tr key={i}>
                                            <td style={{ ...td, fontWeight: 700 }}>
                                                {g.teeth.map(t => fdiToUni(t) ?? t).sort((a, b) => a - b).join(', ')}
                                                <span style={{ color: '#94A3B8', fontWeight: 400, fontSize: 11 }}> (FDI {g.teeth.join(', ')})</span>
                                            </td>
                                            <td style={td}>{g.isPontic ? 'Pontic' : (g.prosthesis || o.indication || '-')}</td>
                                            <td style={td}>{g.material || o.bundles?.[0]?.implantType || '-'}</td>
                                            <td style={td}>{g.shade || '-'}</td>
                                            <td style={td}>{[g.implant && `Implant: ${g.implant}`, g.isBridge && 'Bridge'].filter(Boolean).join(' · ')}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                            {o.requestNote && (
                                <div style={{ background: '#F8FAFC', borderRadius: 8, padding: '10px 14px', fontSize: 13, color: '#475569', lineHeight: 1.6, marginBottom: 12 }}>
                                    <b>Notes:</b> {o.requestNote}
                                </div>
                            )}
                            {o.status === 'returned' && (
                                <div style={{ background: '#FDF3E3', borderRadius: 8, padding: '10px 14px', fontSize: 12.5, color: '#92400E', lineHeight: 1.6, marginBottom: 12 }}>
                                    The design is in your <b>Received results</b> folder. Review it, then confirm or request a revision.
                                    {/* 자동 확정 카운트다운 — 날짜 계산 불가 시 고정 문구 폴백 */}
                                    <span style={{ display: 'block', marginTop: 4, fontWeight: 700 }}>
                                        {autoConfirmDate(o)
                                            ? `Auto-confirms on ${autoConfirmDate(o).toLocaleDateString('en-US')} — please review before then.`
                                            : 'Please review and confirm the returned design.'}
                                    </span>
                                </div>
                            )}
                            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', flexWrap: 'wrap' }}>
                                {o.status === 'returned' && (
                                    <>
                                        <button disabled={busyId === o.id} onClick={() => { setSheetFor(null); confirm(o); }}
                                            style={{ padding: '9px 16px', borderRadius: 8, border: 'none', background: '#0E8A74', color: '#fff', fontSize: 13, fontWeight: 700, cursor: 'pointer' }}>✓ Confirm</button>
                                        <button disabled={busyId === o.id} onClick={() => { setSheetFor(null); setRevisionFor(o); setRevisionReason(''); }}
                                            style={{ padding: '9px 14px', borderRadius: 8, border: '1px solid #DC2626', background: '#fff', color: '#DC2626', fontSize: 13, fontWeight: 700, cursor: 'pointer' }}>Request revision</button>
                                    </>
                                )}
                                <button onClick={() => openOrderSheet(o)}
                                    style={{ padding: '9px 16px', borderRadius: 8, border: '1px solid #E4E6EE', background: '#fff', color: '#5A6072', fontSize: 13, fontWeight: 700, cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 5 }}>
                                    <Printer size={14} /> Print / Save PDF
                                </button>
                            </div>
                        </div>
                    </div>
                );
            })()}

            {/* 초안 검토·보내기 모달 */}
            {draftFor && (
                <div style={{ position: 'fixed', inset: 0, background: 'rgba(15,23,42,.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 50 }} onClick={() => setDraftFor(null)}>
                    <div style={{ background: '#fff', borderRadius: 12, padding: 22, width: '92%', maxWidth: 460 }} onClick={e => e.stopPropagation()}>
                        <div style={{ fontSize: 16, fontWeight: 800, marginBottom: 4 }}>Send {draftFor.caseNo} to {draftFor.toUserName || 'your lab'}</div>
                        <div style={{ fontSize: 12.5, color: '#5A6072', marginBottom: 12 }}>
                            Parsed from your CAD files — teeth <b>{uniStr(draftFor.teeth) || '-'}</b>
                            {draftFor.indication ? <> · {draftFor.indication}</> : null}
                            {draftFor.material ? <> · {draftFor.material}</> : null}
                        </div>
                        {draftFor.folderName && <div style={{ fontSize: 12.5, color: '#5A6072', marginBottom: 6 }}>📁 Your folder: <b>{draftFor.folderName}</b> <span style={{ color: '#94A3B8' }}>(only visible to you)</span></div>}
                        <label style={{ display: 'block', fontSize: 12, fontWeight: 700, color: '#5A6072', margin: '8px 0 4px' }}>Patient reference <span style={{ color: '#94A3B8', fontWeight: 400 }}>(optional — visible to the lab)</span></label>
                        <input value={draftPatientRef} onChange={e => setDraftPatientRef(e.target.value)} placeholder="Your chart # or initials (leave blank to keep private)"
                            style={{ width: '100%', padding: '9px 12px', border: '1px solid #E4E6EE', borderRadius: 8, fontSize: 14, boxSizing: 'border-box' }} />
                        <label style={{ display: 'block', fontSize: 12, fontWeight: 700, color: '#5A6072', margin: '8px 0 4px' }}>Teeth (Universal numbers, comma-separated)</label>
                        <input value={draftTeeth} onChange={e => setDraftTeeth(e.target.value)} placeholder="e.g. 8, 9, 10"
                            style={{ width: '100%', padding: '9px 12px', border: '1px solid #E4E6EE', borderRadius: 8, fontSize: 14, boxSizing: 'border-box' }} />
                        <div style={{ display: 'flex', gap: 10 }}>
                            <div style={{ flex: 1 }}>
                                <label style={{ display: 'block', fontSize: 12, fontWeight: 700, color: '#5A6072', margin: '8px 0 4px' }}>Restoration</label>
                                <input value={draftRestoration} onChange={e => setDraftRestoration(e.target.value)} placeholder="Crown…"
                                    style={{ width: '100%', padding: '9px 12px', border: '1px solid #E4E6EE', borderRadius: 8, fontSize: 14, boxSizing: 'border-box' }} />
                            </div>
                            <div style={{ flex: 1 }}>
                                <label style={{ display: 'block', fontSize: 12, fontWeight: 700, color: '#5A6072', margin: '8px 0 4px' }}>Material</label>
                                <input value={draftMaterial} onChange={e => setDraftMaterial(e.target.value)} placeholder="Zirconia…"
                                    style={{ width: '100%', padding: '9px 12px', border: '1px solid #E4E6EE', borderRadius: 8, fontSize: 14, boxSizing: 'border-box' }} />
                            </div>
                        </div>
                        <label style={{ display: 'block', fontSize: 12, fontWeight: 700, color: '#5A6072', margin: '8px 0 4px' }}>Due date</label>
                        <input type="date" value={draftDue} onChange={e => setDraftDue(e.target.value)}
                            style={{ width: '100%', padding: '9px 12px', border: '1px solid #E4E6EE', borderRadius: 8, fontSize: 14, boxSizing: 'border-box' }} />
                        <label style={{ display: 'block', fontSize: 12, fontWeight: 700, color: '#5A6072', margin: '10px 0 4px' }}>Notes for the lab (translated automatically)</label>
                        <textarea value={draftNote} onChange={e => setDraftNote(e.target.value)}
                            placeholder="e.g. Margin on #21 is critical. Light contacts, please."
                            style={{ width: '100%', minHeight: 80, padding: 10, border: '1px solid #E4E6EE', borderRadius: 8, fontSize: 13.5, boxSizing: 'border-box' }} />
                        <div style={{ display: 'flex', gap: 8, marginTop: 14, justifyContent: 'flex-end' }}>
                            <button onClick={() => setDraftFor(null)} style={{ padding: '9px 14px', borderRadius: 8, border: '1px solid #E4E6EE', background: '#fff', fontSize: 13, cursor: 'pointer' }}>Cancel</button>
                            <button onClick={sendDraft} disabled={busyId === draftFor.id}
                                style={{ padding: '9px 16px', borderRadius: 8, border: 'none', background: '#4F52C7', color: '#fff', fontSize: 13, fontWeight: 700, cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                                <Send size={13} /> {busyId === draftFor.id ? 'Sending…' : 'Send to lab'}
                            </button>
                        </div>
                        <p style={{ fontSize: 11.5, color: '#94A3B8', marginTop: 10, marginBottom: 0 }}>
                            Your Agent uploads the files right after you send (it must be running). Patient names are replaced with the case number.
                        </p>
                    </div>
                </div>
            )}

            {revisionFor && (
                <div style={{ position: 'fixed', inset: 0, background: 'rgba(15,23,42,.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 50 }} onClick={() => setRevisionFor(null)}>
                    <div style={{ background: '#fff', borderRadius: 12, padding: 22, width: '92%', maxWidth: 420 }} onClick={e => e.stopPropagation()}>
                        <div style={{ fontSize: 16, fontWeight: 800, marginBottom: 8 }}>Request revision — {revisionFor.caseNo}</div>
                        <textarea value={revisionReason} onChange={e => setRevisionReason(e.target.value)}
                            placeholder="Describe what needs to change (e.g. margin on #21 is short, contact too tight). Your message will be translated for the lab."
                            style={{ width: '100%', minHeight: 90, padding: 10, border: '1px solid #E4E6EE', borderRadius: 8, fontSize: 13.5, boxSizing: 'border-box' }} />
                        <div style={{ display: 'flex', gap: 8, marginTop: 12, justifyContent: 'flex-end' }}>
                            <button onClick={() => setRevisionFor(null)} style={{ padding: '8px 14px', borderRadius: 8, border: '1px solid #E4E6EE', background: '#fff', fontSize: 13, cursor: 'pointer' }}>Cancel</button>
                            <button onClick={requestRevision} disabled={!revisionReason.trim() || busyId === revisionFor.id}
                                style={{ padding: '8px 14px', borderRadius: 8, border: 'none', background: '#DC2626', color: '#fff', fontSize: 13, fontWeight: 700, cursor: 'pointer' }}>Send request</button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

export default GlobalOrders;
